import { useLiveClinic, liveMapsHref, liveMapsEmbedHref } from "@/lib/clinicLive";

/**
 * Location-keyword SEO section.
 * Visible, semantic content optimised for London / blepharoplasty queries
 * and AI answer engines (GEO). Designed to blend with the existing
 * editorial aesthetic — no design-token violations.
 */
const LocationSEO = () => {
  const clinicNAP = useLiveClinic();
  const mapsHref = liveMapsHref();
  const mapsEmbedHref = liveMapsEmbedHref();
  return (
    <section
      id="london-blepharoplasty"
      aria-labelledby="london-blepharoplasty-heading"
      className="bg-background py-20 md:py-28 px-6 md:px-10 border-t border-border/60"
    >
      <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
            Serving London
          </span>
          <h2
            id="london-blepharoplasty-heading"
            className="font-display text-3xl md:text-4xl mt-4 text-foreground"
          >
            Blepharoplasty in <span className="italic">London</span>
          </h2>
          <p className="mt-6 text-sm text-muted-foreground font-light leading-relaxed">
            A central-London practice for upper and lower eyelid surgery, serving patients across
            the capital and the home counties.
          </p>
        </div>

        <div className="lg:col-span-8 space-y-10 text-foreground/85 font-light leading-relaxed">
          <div className="space-y-3">
            <h3 className="font-display text-xl text-foreground">
              Eyelid surgery for London &amp; the South East
            </h3>
            <p>
              Dr Vahe Clinic provides specialist <strong>blepharoplasty in London</strong> —
              including <strong>upper eyelid surgery</strong>, <strong>lower eyelid surgery</strong>{" "}
              and combined upper-and-lower procedures — from a discreet central-London surgical
              suite. We welcome patients travelling from Mayfair, Marylebone, Knightsbridge,
              Chelsea, Kensington, the City and the wider M25 corridor.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-display text-xl text-foreground">Areas we serve</h3>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              <li>Mayfair</li>
              <li>Marylebone</li>
              <li>Harley Street</li>
              <li>Knightsbridge</li>
              <li>Chelsea</li>
              <li>Kensington</li>
              <li>Belgravia</li>
              <li>The City of London</li>
              <li>Greater London</li>
              <li>Surrey</li>
              <li>Hertfordshire</li>
              <li>Essex</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-display text-xl text-foreground">
              Why patients choose a London eyelid surgeon
            </h3>
            <p>
              London offers immediate access to accredited surgical facilities, experienced
              anaesthetic teams and a concentration of specialist oculoplastic and aesthetic
              expertise. Our practice combines that infrastructure with a single, consistent
              surgeon-led journey from consultation through to aftercare — the standard expected
              of a Harley Street-area private clinic.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-display text-xl text-foreground">Visiting the clinic</h3>
            <address className="not-italic text-foreground/90">
              <strong>{clinicNAP.name}</strong>
              <br />
              {clinicNAP.address.street}
              <br />
              {clinicNAP.address.locality}, {clinicNAP.address.region}{" "}
              {clinicNAP.address.postalCode}
              <br />
              {clinicNAP.address.country}
            </address>
            <p>
              Consultations and surgery take place by appointment only. The clinic sits in
              Chelsea, moments from South Kensington and Sloane Square stations, with discreet
              access for international patients arriving via Heathrow or London City airports.
            </p>
            <a
              href={mapsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
            >
              Open in Google Maps →
            </a>
          </div>

          <div className="pt-4">
            <div className="rounded-2xl overflow-hidden border border-border shadow-soft">
              <iframe
                title={`${clinicNAP.name} — ${clinicNAP.address.street}, ${clinicNAP.address.region} ${clinicNAP.address.postalCode} on Google Maps`}
                src={mapsEmbedHref}
                width="100%"
                height="360"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSEO;
